import { useState } from 'react';
import { PencilSquareIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { useCorrectBackdatedHoursEntry } from '../../hooks/useBackdatedHoursRequests';
import { Modal } from '../common/Modal';
import { formatDisplayDate } from '../../utils/format';
import { toErrorMessage } from '../../utils/toErrorMessage';
import type { ApprovedBackdatedHoursRequest } from '../../services/api/backdatedHoursRequests';

/**
 * MKS-266 — the entries this project's approved backdated-hours requests
 * created, each correctable in place.
 *
 * The month those entries sit in is locked and the entries themselves are
 * `approved`, so nobody — not even the person the hours belong to — can edit
 * them from the timesheet. This table is the one place a PM or an admin can
 * fix a typo without unlocking the whole month. Once an entry is on an issued
 * invoice the row says so instead of offering the button; the money has
 * already gone out and the fix is an invoice correction.
 */
interface BackdatedHoursCorrectionsTableProps {
  requests: ApprovedBackdatedHoursRequest[];
}

export function BackdatedHoursCorrectionsTable({
  requests,
}: BackdatedHoursCorrectionsTableProps) {
  const correctMutation = useCorrectBackdatedHoursEntry();

  const [correcting, setCorrecting] = useState<ApprovedBackdatedHoursRequest | null>(null);
  const [hours, setHours] = useState('');
  const [task, setTask] = useState('');
  const [description, setDescription] = useState('');

  const openCorrect = (req: ApprovedBackdatedHoursRequest) => {
    setCorrecting(req);
    setHours(String(req.entry?.hours ?? req.hours));
    setTask(req.entry?.task ?? '');
    setDescription(req.entry?.description ?? '');
  };

  const parsedHours = Number(hours);
  const hoursValid =
    hours.trim() !== '' && Number.isFinite(parsedHours) && parsedHours > 0 && parsedHours <= 24;

  const handleCorrect = async () => {
    if (!correcting || !hoursValid) return;
    try {
      await correctMutation.mutateAsync({
        id: correcting.id,
        input: { hours: parsedHours, task, description },
      });
      setCorrecting(null);
    } catch {
      // Surfaced in the modal.
    }
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              User
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Date
            </th>
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Hours
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Task
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Description
            </th>
            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {requests.map((req) => (
            <tr key={req.id} className="hover:bg-gray-50">
              <td className="px-4 py-4 whitespace-nowrap">
                <div className="text-sm font-medium text-gray-900">
                  {req.userName || req.userEmail}
                </div>
                <div className="text-xs text-gray-500">{req.userEmail}</div>
              </td>
              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                {formatDisplayDate(req.entry?.date ?? req.date)}
              </td>
              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                {req.entry?.hours ?? '—'}
                {req.entry && req.entry.hours !== req.hours && (
                  <span className="block text-[11px] font-normal text-gray-400">
                    requested {req.hours}
                  </span>
                )}
              </td>
              <td className="px-4 py-4 text-sm text-gray-500">{req.entry?.task || '—'}</td>
              <td className="px-4 py-4 text-sm text-gray-500 max-w-xs">
                <span className="block truncate" title={req.entry?.description ?? undefined}>
                  {req.entry?.description || '—'}
                </span>
              </td>
              <td className="px-4 py-4 whitespace-nowrap text-right">
                {req.entry?.invoiced ? (
                  <span
                    className="inline-flex items-center gap-1 text-xs text-gray-500"
                    title="Already on an issued invoice — correct the invoice instead."
                  >
                    <LockClosedIcon className="w-3.5 h-3.5" />
                    Invoiced
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => openCorrect(req)}
                    disabled={!req.entry}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                  >
                    <PencilSquareIcon className="w-3.5 h-3.5" />
                    Correct
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <Modal
        isOpen={!!correcting}
        onClose={() => setCorrecting(null)}
        title={`Correct ${formatDisplayDate(correcting?.entry?.date ?? correcting?.date ?? null)}`}
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            This corrects the entry on{' '}
            <span className="font-medium">{correcting?.userName || correcting?.userEmail}</span>
            &rsquo;s timesheet even though {correcting?.month} is locked. The change is recorded in
            the audit trail, and {correcting?.userName || 'the employee'} still cannot edit the
            entry — their way is a new request.
          </p>

          <div>
            <label
              htmlFor="backdated-correct-hours"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Hours
            </label>
            <input
              id="backdated-correct-hours"
              type="number"
              step="0.1"
              min="0.1"
              max="24"
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            {!hoursValid && (
              <p className="mt-1 text-xs text-red-600">Enter hours between 0 and 24.</p>
            )}
          </div>

          <div>
            <label
              htmlFor="backdated-correct-task"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Task
            </label>
            <input
              id="backdated-correct-task"
              type="text"
              maxLength={255}
              value={task}
              onChange={(e) => setTask(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label
              htmlFor="backdated-correct-description"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Description
            </label>
            <textarea
              id="backdated-correct-description"
              rows={2}
              maxLength={1000}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {correctMutation.isError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
              {toErrorMessage(correctMutation.error, 'Failed to correct the entry')}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setCorrecting(null)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleCorrect}
              disabled={correctMutation.isPending || !hoursValid}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {correctMutation.isPending ? 'Saving…' : 'Save correction'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default BackdatedHoursCorrectionsTable;
